import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { fillRoundedRect, strokeRoundedRect } from "../../utils/draw";
import { drawFrame } from "../entities/FrameAccessory";
import type { BattleScoreResult } from "../systems/Battles";
import type { RaceRun, RaceState } from "../systems/Races";
import type { Medal } from "../systems/Score";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

const MEDAL_COLORS: Record<Medal, string> = {
  none: "#777",
  bronze: "#cd7f32",
  silver: "#c0c0c0",
  gold: "#ffd54a",
  platinum: "#a8e3ff",
};

function describeBattle(result: BattleScoreResult | undefined, error?: string): string {
  if (!result) {
    return error === "already_played"
      ? "Battle: already played"
      : error === "not_active"
        ? "Battle: no longer active"
        : "Battle: couldn't submit";
  }
  if (!result.settled) return "Battle: score in — waiting for opponent";
  if (result.outcome === "win") return `Battle: WON +${result.payout} flies!`;
  if (result.outcome === "lose") return `Battle: lost (−${result.bet} flies)`;
  return `Battle: tie — ${result.bet} flies back`;
}

function describeRace(st: RaceState, myName: string, run: RaceRun): string {
  const me = st.self.score;
  const opp = st.other.score;
  const tie = st.winner === "tie";
  const won = (st.winner ?? "").toLowerCase() === myName.toLowerCase();
  const head = tie
    ? `Race: TIE ${me}–${opp}`
    : won
      ? `Race: WON ${me}–${opp}!`
      : `Race: lost ${me}–${opp}`;
  if (!run.battleId) return head;
  const stake = run.stake ?? 0;
  const flies = won ? `+${stake * 2}` : tie ? `+${stake}` : `−${stake}`;
  return `${head}  (${flies} flies)`;
}

export class GameOverScene implements Scene {
  private cooldown = 0.4;
  private rank: number | null = null;
  private battleLine = "";
  private raceLine = "";
  private unlockedLine = "";
  private racePollAccum = 0;
  private raceResolved = false;

  constructor(
    private readonly ctxMgr: GameContext,
    private readonly result: { isNewHigh: boolean; medal: Medal },
    battleId: string | null = null,
    private readonly race: RaceRun | null = null,
  ) {
    const name = ctxMgr.settings.playerName;
    const token = ctxMgr.settings.authToken;
    if (name && token && (ctxMgr.score.current > 0 || ctxMgr.score.flies > 0)) {
      void ctxMgr.leaderboard
        .submit(name, token, ctxMgr.score.current, ctxMgr.score.flies)
        .then((r) => {
          this.rank = r.rank;
          if (r.unlocked.length > 0) {
            this.unlockedLine =
              r.unlocked.length === 1
                ? `Unlocked the ${r.unlocked[0]} collection!`
                : `Unlocked: ${r.unlocked.join(", ")}!`;
            void this.ctxMgr.shop.refresh();
          }
        });
    }

    // This run counted for a battle — record its score and show the result.
    if (battleId && name && token) {
      this.battleLine = "Battle: submitting…";
      void ctxMgr.battles
        .submitScore(name, token, battleId, ctxMgr.score.current)
        .then((r) => {
          this.ctxMgr.battles.pendingBattleId = null;
          this.battleLine = describeBattle(r.ok ? r.result : undefined, r.error);
          if (r.ok) void this.ctxMgr.shop.refresh();
        });
    }

    if (this.race) this.raceLine = "Race: waiting for opponent to finish…";
  }

  enter(): void {
    this.ctxMgr.buttons.clear();
  }

  onFlap(): void {
    if (this.cooldown > 0) return;
    this.ctxMgr.goTo(new MenuScene(this.ctxMgr));
  }

  update(dt: number): void {
    if (this.cooldown > 0) this.cooldown = Math.max(0, this.cooldown - dt);

    // Poll the race until the opponent also finishes, then show the verdict.
    if (this.race && !this.raceResolved) {
      this.racePollAccum += dt;
      if (this.racePollAccum >= 0.6) {
        this.racePollAccum = 0;
        const { playerName, authToken } = this.ctxMgr.settings;
        const run = this.race;
        void this.ctxMgr.races.poll(playerName, authToken, run.id).then((st) => {
          if (st && st.status === "done") {
            this.raceResolved = true;
            this.raceLine = describeRace(st, playerName, run);
            if (run.battleId) void this.ctxMgr.shop.refresh(); // pot was paid out
          }
        });
      }
    }
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);

    const cx = VIRTUAL_WIDTH / 2;
    const cardY = VIRTUAL_HEIGHT * 0.22;
    const cardW = 240;
    const cardH = 250;
    ctx.fillStyle = "#f5e9c8";
    fillRoundedRect(ctx, cx - cardW / 2, cardY, cardW, cardH, 12);
    ctx.strokeStyle = "#7a5e2a";
    ctx.lineWidth = 3;
    strokeRoundedRect(ctx, cx - cardW / 2, cardY, cardW, cardH, 12);

    const frame = this.ctxMgr.shop.getEquippedFrameRender();
    if (frame) {
      drawFrame(
        ctx,
        frame,
        { x: cx - cardW / 2 - 4, y: cardY - 4, w: cardW + 8, h: cardH + 8, radius: 14 },
        performance.now() / 1000,
      );
    }

    ctx.fillStyle = "#7a5e2a";
    ctx.font = "bold 22px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Game Over", cx, cardY + 30);

    const medalCx = cx - 70;
    const medalCy = cardY + 95;
    ctx.fillStyle = MEDAL_COLORS[this.result.medal];
    ctx.beginPath();
    ctx.arc(medalCx, medalCy, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#7a5e2a";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = "#7a5e2a";
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText(this.result.medal.toUpperCase(), medalCx, medalCy + 50);

    ctx.textAlign = "right";
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillText("score", cx + 90, cardY + 80);
    ctx.fillText("best", cx + 90, cardY + 120);
    ctx.font = "bold 22px system-ui, sans-serif";
    ctx.fillText(String(this.ctxMgr.score.current), cx + 90, cardY + 100);
    ctx.fillText(String(this.ctxMgr.score.high), cx + 90, cardY + 140);

    if (this.result.isNewHigh) {
      ctx.fillStyle = "#e53935";
      ctx.font = "bold 12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("NEW BEST!", cx, cardY + 170);
    }

    if (this.rank !== null) {
      ctx.fillStyle = "#7a5e2a";
      ctx.font = "bold 13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`Leaderboard rank: #${this.rank}`, cx, cardY + 200);
    }

    const versusLine = this.battleLine || this.raceLine;
    if (versusLine) {
      ctx.fillStyle = "#8e1a1a";
      ctx.font = "bold 12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(versusLine, cx, cardY + 188);
    }

    const playerName = this.ctxMgr.settings.playerName;
    if (playerName) {
      ctx.fillStyle = "#7a5e2a";
      ctx.font = "11px system-ui, sans-serif";
      ctx.fillText(`as ${playerName}`, cx, cardY + 220);
    }

    if (this.unlockedLine) {
      ctx.fillStyle = "#1b7a3a";
      ctx.font = "bold 12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(this.unlockedLine, cx, cardY + 238);
    }

    ctx.fillStyle = "#7a5e2a";
    ctx.textAlign = "center";
    ctx.font = "13px system-ui, sans-serif";
    ctx.fillText("tap to continue", cx, cardY + cardH + 30);
  }
}
